import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { NetworthPeriod } from "../../hooks/networth";
import { NetworthChart } from "../dashboard/NetworthChart";

interface Props {
  open: boolean;
  onClose: () => void;
  defaultPeriod?: NetworthPeriod;
  onDatapointClick?: (stichtag: string) => void;
}

const PERIODS: NetworthPeriod[] = ["1M", "3M", "6M", "YTD", "1J", "3J", "5J", "all"];

const PERIOD_LABEL: Record<NetworthPeriod, string> = {
  "1M": "1M",
  "3M": "3M",
  "6M": "6M",
  YTD: "YTD",
  "1J": "1J",
  "3J": "3J",
  "5J": "5J",
  all: "Max",
};

export function NetworthChartFullscreen({
  open,
  onClose,
  defaultPeriod = "1J",
  onDatapointClick,
}: Props) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<NetworthPeriod>(defaultPeriod);
  const [showYoy, setShowYoy] = useState(false);
  const [chartHeight, setChartHeight] = useState(400);

  useEffect(() => {
    if (!open) return;
    const update = () => setChartHeight(Math.floor(window.innerHeight * 0.6));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: "var(--bg)" }}
      aria-label={t("dashboard.networth_chart.title", { defaultValue: "Vermögensverlauf" })}
    >
      <header
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: "var(--border)", background: "var(--card)" }}
      >
        <button
          type="button"
          onClick={onClose}
          className="min-w-[44px] min-h-[44px] flex items-center justify-center -ml-2"
          aria-label={t("common.close", { defaultValue: "Schließen" })}
          style={{ color: "var(--text)" }}
        >
          <svg
            className="w-6 h-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        <h2 className="text-base font-semibold flex-1 text-center -ml-10">
          {t("dashboard.networth_chart.title", { defaultValue: "Vermögensverlauf" })}
        </h2>
      </header>

      <div className="overflow-x-auto border-b" style={{ borderColor: "var(--border)" }}>
        <div className="flex gap-1 px-4 py-2 w-max">
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className="px-3 py-2 text-sm rounded-md font-medium min-h-[44px] whitespace-nowrap"
              style={
                period === p
                  ? { background: "var(--text)", color: "var(--bg)" }
                  : { color: "var(--muted)", background: "var(--card-elev)" }
              }
            >
              {PERIOD_LABEL[p]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <NetworthChart
          key={period}
          embedded
          defaultPeriod={period}
          height={chartHeight}
          showYoy={showYoy}
          hidePicker
          onDatapointClick={onDatapointClick}
        />
        <label className="flex items-center gap-2 mt-4 text-sm">
          <input
            type="checkbox"
            checked={showYoy}
            onChange={(e) => setShowYoy(e.target.checked)}
            className="w-4 h-4"
          />
          <span>
            {t("dashboard.networth_chart.show_yoy", {
              defaultValue: "Vorjahresvergleich anzeigen",
            })}
          </span>
        </label>
      </div>
    </div>
  );
}
